/**
 * Accounts: one row per zInspector property in an account group, built from everything the
 * accounts list and page show. Pure, so it can be tested without the API.
 *
 * Where each field comes from (zcrm-docs/reference/account-fields.md):
 * - the property: name, address parts, market (user group), FileMaker ID (AccountingID)
 * - the "Account" zData area: type, health, doors, phone, website, PM software, notes…
 * - the leases: status (prospect / onboarding / active / winding down / former), signed dates
 * - the open tasks: account manager (journal task), next step, open deals and tickets, last touch
 */
import type { Identifier } from "ra-core";

import { type CommercialTerms } from "./commercialTerms";
import {
  ACCOUNT_DETAILS,
  ACCOUNT_STATUS_TASK_FIELD,
  COMMERCIAL_AREA,
  COMMERCIAL_TERMS,
  ACCOUNT_STATUSES,
  JOURNAL_TASK_PREFIX,
  LEASE_STATUS_TO_ACCOUNT_STATUS,
  REQUIRED_ACCOUNT_INFO,
  TASK_TYPES,
  type AccountDetailKey,
  type AccountStatusId,
} from "./crmConfig";
import {
  propertyToCompany,
  type ZcrmCompany,
  type ZiLease,
  type ZiProperty,
  type ZiTask,
  type ZiZDataRow,
} from "./mappers";

export type AccountView = "clients" | "prospects" | "former";

export type ZcrmAccount = ZcrmCompany &
  Record<AccountDetailKey, string> & {
    status: AccountStatusId;
    /** Where the status comes from: set by hand, or the engagement (lease). */
    status_source: "manual" | "engagement";
    /** The status the engagement alone gives, shown when a status is set by hand. */
    engagement_status: AccountStatusId;
    view: AccountView;
    /** Effective date of the engagement (Lease.LeaseStart). */
    signed_on: string | null;
    /** Days since signing, for onboarding accounts (activation is measured in days). */
    days_since_signed: number | null;
    agreement_state: string;
    main_contact_name: string;
    main_contact_id: Identifier | null;
    main_contact_email: string;
    main_contact_phone: string;
    /** The account's "Account journal" task: its manager is the account manager. */
    journal_task_id: Identifier | null;
    /** Serviceable doors as a number, for sorting and range filters. */
    doors_total: number | null;
    /** Doors served as a share of serviceable doors, 0-100; null when either is unknown. */
    doors_share: number | null;
    /** The key facts this account is missing (labels), and whether any is. */
    missing_info: string[];
    needs_info: boolean;
    /** A prospect nobody has worked on: no deal, call or task, ever (recently). */
    is_untouched: boolean;
    account_manager_id: Identifier | null;
    next_step: string;
    next_step_due: string | null;
    /** The task the next step comes from; only follow-ups can be edited from zCRM. */
    next_step_task_id: Identifier | null;
    next_step_source: "follow-up" | "onboarding" | null;
    open_deals: number;
    open_tickets: number;
    last_touch: string | null;
    nb_contacts: number;
    /** What the client pays and how: the "Commercial" zData area. */
    commercial: CommercialTerms;
    /** "Account" details zCRM has no field for yet, shown as they are on the account page. */
    account_details: { label: string; value: string }[];
    /** Lower-cased text the list search matches: name, FileMaker ID, city, contact names. */
    search_text: string;
  };

export interface AccountSources {
  properties: ZiProperty[];
  leases: ZiLease[];
  /** Open tasks of every type (`active=true`). */
  openTasks: ZiTask[];
  /** Tasks of every type updated recently, closed ones included (logged calls, won deals). */
  recentTasks?: ZiTask[];
  zData: Map<Identifier, ZiZDataRow[]>;
  /** Contact names per property, for the count and the search. */
  contactNames: Map<Identifier, string[]>;
  today?: Date;
}

const DAY_MS = 24 * 60 * 60 * 1000;

const groupBy = <T, K>(items: T[], key: (item: T) => K) => {
  const groups = new Map<K, T[]>();
  for (const item of items) {
    const k = key(item);
    groups.set(k, [...(groups.get(k) ?? []), item]);
  }
  return groups;
};

const statusFromLeases = (leases: ZiLease[]): AccountStatusId => {
  const active = leases.filter((lease) => !lease.IsInactive);
  for (const [leaseStatus, accountStatus] of LEASE_STATUS_TO_ACCOUNT_STATUS) {
    if (active.some((lease) => lease.Status === leaseStatus)) {
      return accountStatus;
    }
  }
  return leases.length > 0 ? "former" : "prospect";
};

/** The lease that describes the relationship: the live one, else the most recent. */
const currentLease = (leases: ZiLease[]): ZiLease | undefined =>
  leases.find((lease) => !lease.IsInactive) ??
  [...leases].sort((a, b) =>
    (b.LeaseStart ?? "").localeCompare(a.LeaseStart ?? ""),
  )[0];

const isJournal = (task: ZiTask) =>
  (task.Description ?? "").startsWith(JOURNAL_TASK_PREFIX);

const latest = (dates: (string | null | undefined)[]): string | null =>
  dates
    .filter((d): d is string => !!d)
    .sort()
    .at(-1) ?? null;

/** The value of each detail in a zData area, by key ("" when not set). */
const areaValues = <K extends string>(
  rows: ZiZDataRow[],
  area: string,
  details: readonly { key: K; detail: string }[],
) =>
  Object.fromEntries(
    details.map(({ key, detail }) => [
      key,
      rows.find((row) => row.area_name === area && row.detail === detail)
        ?.value ?? "",
    ]),
  ) as Record<K, string>;

const KNOWN_DETAILS = new Set<string>(ACCOUNT_DETAILS.map((d) => d.detail));

const otherDetails = (rows: ZiZDataRow[] = []) =>
  rows
    .filter(
      (row) => row.area_name === "Account" && !KNOWN_DETAILS.has(row.detail),
    )
    .map((row) => ({ label: row.detail, value: row.value }));

/**
 * The next thing to do: the soonest dated open follow-up (a call, meeting or to-do someone
 * scheduled; editable), else the onboarding playbook's current to-do (edited in zInspector).
 */
const nextStep = (
  tasks: ZiTask[],
): {
  text: string;
  due: string | null;
  taskId: Identifier | null;
  source: "follow-up" | "onboarding" | null;
} => {
  const followUp = tasks
    .filter(
      (task) =>
        task.Action === TASK_TYPES.meeting &&
        !isJournal(task) &&
        task.StartDate,
    )
    .sort((a, b) => (a.StartDate ?? "").localeCompare(b.StartDate ?? ""))[0];
  if (followUp) {
    return {
      text: followUp.Description ?? "",
      due: followUp.StartDate ?? null,
      taskId: followUp.id,
      source: "follow-up",
    };
  }
  const onboarding = tasks.find(
    (task) => task.Action === TASK_TYPES.implementation && task.NextToDoName,
  );
  return onboarding
    ? {
        text: onboarding.NextToDoName ?? "",
        due: onboarding.EndDate ?? null,
        taskId: onboarding.id,
        source: "onboarding",
      }
    : { text: "", due: null, taskId: null, source: null };
};

const toNumber = (value: string) => {
  const parsed = Number(value.replace(/[,\s]/g, ""));
  return value.trim() && Number.isFinite(parsed) ? parsed : null;
};

const doorsShare = (served: string, total: string): number | null => {
  const s = toNumber(served);
  const t = toNumber(total);
  return s === null || !t ? null : Math.round((s / t) * 100);
};

/** The REQUIRED_ACCOUNT_INFO facts this account lacks, by label. */
const missingAccountInfo = (
  account: Record<string, unknown> & { view: AccountView },
): string[] =>
  REQUIRED_ACCOUNT_INFO.filter(
    ({ key, forProspects }) =>
      (forProspects || account.view !== "prospects") &&
      !String(account[key] ?? "").trim(),
  ).map(({ label }) => label);

export const buildAccounts = ({
  properties,
  leases,
  openTasks,
  recentTasks = [],
  zData,
  contactNames,
  today = new Date(),
}: AccountSources): ZcrmAccount[] => {
  const leasesByProperty = groupBy(leases, (lease) => lease.Property);
  const tasksByProperty = groupBy(openTasks, (task) => task.Property);
  const recentByProperty = groupBy(recentTasks, (task) => task.Property);

  return properties.map((property) => {
    const company = propertyToCompany(property);
    const propertyLeases = leasesByProperty.get(property.id) ?? [];
    const tasks = tasksByProperty.get(property.id) ?? [];
    const engagementStatus = statusFromLeases(propertyLeases);
    const journalForStatus = tasks.find(isJournal);
    const manualStatus = String(
      (journalForStatus as Record<string, unknown> | undefined)?.[
        ACCOUNT_STATUS_TASK_FIELD
      ] ?? "",
    );
    const isManual = ACCOUNT_STATUSES.some((s) => s.id === manualStatus);
    const status = isManual
      ? (manualStatus as AccountStatusId)
      : engagementStatus;
    const lease = currentLease(propertyLeases);
    const signedOn = lease?.LeaseStart ?? null;
    const journal = tasks.find(isJournal);
    const newestDeal = tasks
      .filter((task) => task.Action === TASK_TYPES.deal)
      .sort((a, b) =>
        (b.LastModified ?? "").localeCompare(a.LastModified ?? ""),
      )[0];
    const step = nextStep(tasks);
    const names = contactNames.get(property.id) ?? [];
    const recent = recentByProperty.get(property.id) ?? [];
    const tenant = lease?.PrimaryTenant;
    const rows = zData.get(property.id) ?? [];
    const details = areaValues(rows, "Account", ACCOUNT_DETAILS);

    const account = {
      ...company,
      ...details,
      website: details.website,
      phone_number: details.office_phone,
      status,
      status_source: isManual ? ("manual" as const) : ("engagement" as const),
      engagement_status: engagementStatus,
      view: ACCOUNT_STATUSES.find((s) => s.id === status)?.view ?? "prospects",
      signed_on: signedOn,
      days_since_signed:
        status === "onboarding" && signedOn
          ? Math.max(
              0,
              Math.floor((today.getTime() - Date.parse(signedOn)) / DAY_MS),
            )
          : null,
      agreement_state: lease?.SigStatus ?? "",
      main_contact_name: tenant
        ? `${tenant.FirstName ?? ""} ${tenant.LastName ?? ""}`.trim()
        : "",
      main_contact_id: tenant?.id ?? null,
      main_contact_email: tenant?.Email ?? "",
      main_contact_phone: tenant?.MobilePhone || tenant?.OfficePhone || "",
      journal_task_id: journal?.id ?? null,
      doors_total: toNumber(details.serviceable_doors),
      doors_share: doorsShare(details.doors_served, details.serviceable_doors),
      account_manager_id: journal?.ManagedBy ?? newestDeal?.ManagedBy ?? null,
      next_step: step.text,
      next_step_due: step.due,
      next_step_task_id: step.taskId,
      next_step_source: step.source,
      open_deals: tasks.filter((task) => task.Action === TASK_TYPES.deal)
        .length,
      open_tickets: tasks.filter((task) => task.Action === TASK_TYPES.ticket)
        .length,
      last_touch: latest([
        ...tasks.map((task) => task.LastModified),
        ...recent.map((task) => task.LastModified),
        property.LastActivity,
      ]),
      nb_contacts: names.length,
      account_details: otherDetails(rows),
      commercial: areaValues(rows, COMMERCIAL_AREA, COMMERCIAL_TERMS),
      search_text: [
        property.Name,
        property.AccountingID,
        company.city,
        details.former_names,
        details.pipedrive_id,
        ...names,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase(),
    };
    const missingInfo = missingAccountInfo(account);
    return {
      ...account,
      missing_info: missingInfo,
      needs_info: missingInfo.length > 0,
      is_untouched:
        account.view === "prospects" &&
        tasks.length === 0 &&
        recent.length === 0,
    };
  });
};
